import type { ModuleManifest } from '@nexus/core';
import type { ComponentType, ReactNode } from 'react';
import type { DocumentProvider } from '../documents/provider';

/** Texts of a module, below its ID: keys `hr.absences.title` are `{ absences: { title } }`. */
export type ModuleMessages = { [key: string]: string | ModuleMessages };

export interface WebPage {
  /** Full path, starting with the module path, e.g. `/hr/absences/$absenceId`. */
  path: string;
  /** Loaded on first visit, so every module is its own bundle (docs/ui/responsive.md). */
  load: () => Promise<{ default: () => ReactNode }>;
  /** Required permission. The API checks again; this only hides the page. */
  permission?: string;
}

/** Markdown of a guide, loaded on first use: `() => import('./guides/cars.de.md?raw')`. */
export type GuideSource = () => Promise<string | { default: string }>;

/** A guide of the help area `/help` (docs/architecture/guides.md). */
export interface WebGuide {
  /** `<module id>.<name>`; the guide opens at `/help/<id>`. */
  id: string;
  /** i18n keys of the title and a one-line summary. */
  title: string;
  summary: string;
  /** Shown only with this permission. Without it, everyone who sees the module sees the guide. */
  permission?: string;
  /** Pages whose header leads to this guide, as in `pages` (`/fleet/cars/$carId`). */
  pages?: readonly string[];
  /** The text in both languages. The title comes from `title`, the text starts with `##`. */
  content: { de: GuideSource; en: GuideSource };
}

/**
 * Content of a start page tile declared under `dashboard` in the manifest. The kernel renders
 * frame, heading and link, loading and error state; the component renders what is inside,
 * including its empty state (docs/architecture/dashboard.md).
 */
export interface WebDashboardTile {
  /** Key of the tile in the manifest, e.g. `fleet.inspections-due`. */
  key: string;
  /** Loaded when the tile shows for the first time, like pages. */
  load: () => Promise<{ default: () => ReactNode }>;
}

/** What a section on the detail page of another module gets (docs/architecture/modules.md). */
export interface RecordSectionProps {
  /** ID of the record the page shows, e.g. the CRM organisation. */
  recordId: string;
}

/**
 * Content of a section declared under `recordSections` in the manifest. The kernel renders the
 * heading, loading and error state; the component renders the content and its empty state.
 */
export interface WebRecordSection {
  /** Key of the section in the manifest, e.g. `projects.customer-projects`. */
  key: string;
  /** Loaded when the section shows for the first time, like pages. */
  load: () => Promise<{ default: ComponentType<RecordSectionProps> }>;
}

/** Everything the web app needs from a module. Defined in `src/web/index.ts` of the module. */
export interface WebModule {
  manifest: ModuleManifest;
  messages: { de: ModuleMessages; en: ModuleMessages };
  pages: readonly WebPage[];
  /** Guides of the module in the help area; their pages get a button "Anleitung". */
  guides?: readonly WebGuide[];
  /** Components of the tiles the manifest declares under `dashboard`. */
  dashboard?: readonly WebDashboardTile[];
  /** Components of the sections the manifest declares under `recordSections`. */
  recordSections?: readonly WebRecordSection[];
  /**
   * Data providers of the documents the manifest declares under `documents`, by document key,
   * loaded when a document is created or previewed (docs/architecture/documents.md).
   */
  documents?: Readonly<Record<string, () => Promise<{ default: DocumentProvider }>>>;
}

export function defineWebModule<const T extends WebModule>(module: T): T {
  return module;
}
